/* eslint-disable react/require-default-props */
import { forwardRef, useEffect, useRef, useState } from 'react'

import Center from '@components/Center/Center'
import IconButton from '@components/IconButton/IconButton'
import { Manufacturer } from '@domain'
import { faSearch } from '@fortawesome/pro-solid-svg-icons'
import { Box, Stack, TextFieldProps, Typography, useTheme } from '@mui/material'
import errorShake from '@utils/animations'
import { Controller, FieldError, useFormContext } from 'react-hook-form'

import Dialog from './components/Dialog'
import ListItem from './components/ListItem'

type Props = Omit<TextFieldProps, 'error'> & {
	rightSlot?: React.ReactNode
	required?: boolean
	onChange?: (value: Manufacturer[]) => void
	error?: FieldError
}

// FIXME: Make this a generic RHFListInput component similar to RHFTreeInput when we
// fetch/display other list types.
export default function RHFMfgSearchInput({ name, required = false, ...props }: Props) {
	const { control } = useFormContext()

	return (
		<Controller
			control={control}
			name={name}
			rules={{ required }}
			render={({ field, fieldState }) => (
				<Input {...field} required={required} name={name} {...props} error={fieldState.error} />
			)}
		/>
	)
}

// --------------------------------------------------------
// Rendered Input
// --------------------------------------------------------
const Input = forwardRef<HTMLDivElement, Props>(
	(
		{
			disabled = false,
			onChange = null,
			error = null,
			value = [],
			required = false,
			rightSlot = null,
			label,
			name,
		}: Props,
		ref
	) => {
		const [list, setList] = useState<Manufacturer[]>(value as Manufacturer[])
		const [open, setOpen] = useState(false)

		const theme = useTheme()

		const hasError = Boolean(error)
		const fieldRef = useRef<HTMLDivElement>(null)

		useEffect(() => {
			if (hasError && fieldRef.current) {
				fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		}, [hasError])

		useEffect(() => {
			onChange(list)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [list])

		const handleAdd = (items: Manufacturer[]) => {
			const newList = [...list, ...items]
			newList.sort((a, b) => a.name.localeCompare(b.name))
			setList(newList)
		}

		const handleDelete = (id: Id) => {
			const newList = list.filter((item) => item.id !== id)
			setList(newList)
		}

		return (
			<Box
				ref={fieldRef}
				sx={{
					'@keyframes shake': errorShake,
					animation: error ? 'shake 0.25s' : null,
				}}
			>
				<Stack gap={1}>
					<Stack
						direction="row"
						sx={{ width: '100%' }}
						spacing={1}
						alignItems="center"
						justifyContent="space-between"
					>
						<Typography color={hasError ? 'error' : 'textPrimary'} variant="body1">
							{!required ? label : `${label} *`}
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center">
							{rightSlot}
							<IconButton
								disabled={disabled}
								onClick={() => setOpen(true)}
								size="small"
								icon={faSearch}
							/>
						</Stack>
					</Stack>

					<Dialog name={name} open={open} onClose={() => setOpen(false)} onSave={handleAdd} />

					<Box
						tabIndex={0} // Needed to allow focus styles
						ref={ref}
						sx={{
							minHeight: '6rem',
							maxHeight: '20rem',
							overflowY: 'auto',
							border: `${hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.grey[400]}`}`,
							borderRadius: '0.25rem',
							cursor: `${disabled || list.length ? 'default' : 'pointer'}`,
							p: 1,
							'&:hover': {
								borderColor: hasError ? theme.palette.error.main : theme.palette.text.primary,
							},
						}}
						onClick={disabled || list.length ? null : () => setOpen(true)}
					>
						{!list.length && (
							<Center sx={{ height: 'calc(6rem - 16px)' }}>
								{/* 16px = padding top + bottom */}
								<Typography color="textSecondary" variant="body2">
									Search...
								</Typography>
							</Center>
						)}
						<List list={list} onDelete={(id) => handleDelete(id)} />
					</Box>
					{error && <Typography color="error.main">{error.message}</Typography>}
				</Stack>
			</Box>
		)
	}
)

/**
 * Displays a list of items.
 */
function List({ list, onDelete }: { list: Manufacturer[]; onDelete: (id: Id) => void }) {
	return (
		<Stack spacing={1} sx={{ height: '100%' }}>
			{list.map((item: any) => (
				<ListItem
					key={item.id}
					name={item.name}
					id={item.id}
					plid={item.plid}
					onDelete={() => {
						onDelete(item.id)
					}}
				/>
			))}
		</Stack>
	)
}
